import React from 'react';
import { ArrowLeft } from 'lucide-react';
interface PrivacyPolicyProps {
  onBack: () => void;
}
export function PrivacyPolicy({
  onBack
}: PrivacyPolicyProps) {
  return <div className="min-h-screen bg-white" data-id="element-352">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 z-50" data-id="element-353">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-354">
          <button onClick={onBack} className="flex items-center text-gray-600 hover:text-[#00E676] transition-colors" data-id="element-355">
            <ArrowLeft className="w-5 h-5 mr-2" data-id="element-356" />
            Back to Home
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-357">
        <div className="space-y-8 text-gray-700 leading-relaxed" data-id="element-358">
          <div data-id="element-359">
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-id="element-360">
              Privacy Policy
            </h1>
            <p className="text-sm text-gray-600" data-id="element-361">
              Effective Date: December 16, 2025
            </p>
          </div>

          <div className="prose prose-lg max-w-none" data-id="element-362">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4" data-id="element-363">
              BRIXEN FUNDING LLC - PRIVACY POLICY
            </h2>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3" data-id="element-364">
              1. INTRODUCTION
            </h3>
            <p data-id="element-365">
              Brixen Funding LLC ("we," "us," or "our") respects your privacy
              and is committed to protecting your personal data. This Privacy
              Policy explains how we collect, use, disclose, and safeguard your
              information when you visit our website and use our brokerage
              services. By using our Site, you consent to the data practices
              described in this policy.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3" data-id="element-366">
              2. INFORMATION WE COLLECT
            </h3>
            <p className="mb-3" data-id="element-367">
              We collect various types of information to facilitate the business
              funding process:
            </p>
            <p className="mb-2" data-id="element-368">
              <strong data-id="element-369">Personal Identification Information:</strong> Name, email
              address, phone number, business address, date of birth, Social
              Security Number (SSN), and driver's license number.
            </p>
            <p className="mb-2" data-id="element-370">
              <strong data-id="element-371">Business & Financial Information:</strong> Business name,
              Employer Identification Number (EIN), business start date,
              industry type, annual revenue, bank statements, tax returns,
              credit card processing statements, and credit history.
            </p>
            <p data-id="element-372">
              <strong data-id="element-373">Technical Usage Data:</strong> IP address, browser type,
              operating system, access times, and pages viewed, collected
              automatically via cookies and tracking technologies.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3" data-id="element-374">
              3. HOW WE USE YOUR INFORMATION
            </h3>
            <p className="mb-3" data-id="element-375">
              We use the information we collect for the following business
              purposes:
            </p>
            <p className="mb-2" data-id="element-376">
              <strong data-id="element-377">Funding Facilitation:</strong> To evaluate your
              eligibility for financing and submit your application to our
              network of lenders and Funding Partners.
            </p>
            <p className="mb-2" data-id="element-378">
              <strong data-id="element-379">Communication:</strong> To contact you via email, phone,
              SMS, or WhatsApp regarding your application status, missing
              documents, or offers.
            </p>
            <p className="mb-2" data-id="element-380">
              <strong data-id="element-381">Marketing:</strong> To send you information about new
              funding products, services, or special offers (unless you opt
              out).
            </p>
            <p data-id="element-382">
              <strong data-id="element-383">Security & Compliance:</strong> To verify your identity,
              prevent fraud, and comply with legal obligations (such as KYC and
              AML laws).
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3" data-id="element-384">
              4. SHARING YOUR INFORMATION & MESSAGING DISCLOSURES
            </h3>
            <p className="mb-3" data-id="element-385">
              As a broker, sharing information is essential to our service. We
              may share your general business data with Funding Partners,
              Service Providers, and Legal Authorities as necessary to secure
              funding.
            </p>
            <p className="mb-3" data-id="element-386">
              However, we adhere to strict standards regarding your mobile data:
            </p>

            <h4 className="text-lg font-bold text-gray-900 mt-4 mb-2" data-id="element-387">
              Data Sharing
            </h4>
            <ul className="list-disc list-inside space-y-2 mb-4" data-id="element-388">
              <li data-id="element-389">
                Customer data is not shared with 3rd parties for promotional or
                marketing purposes.
              </li>
              <li data-id="element-390">
                Mobile opt-in and consent are never shared with anyone for any
                purpose. Any information sharing that may be mentioned elsewhere
                in this policy excludes mobile opt-in data.
              </li>
            </ul>

            <h4 className="text-lg font-bold text-gray-900 mt-4 mb-2" data-id="element-391">
              Messaging Terms and Conditions
            </h4>
            <p data-id="element-392">
              By providing your phone number and agreeing to receive texts, you
              consent to receive text messages from BRIXEN FUNDING LLC, from
              (800) 326-0670 regarding customer care. Consent is not a condition
              of purchase. Message frequency varies. Message & data rates may
              apply. You can reply STOP to unsubscribe at any time or HELP for
              assistance. You can also contact us at (800) 326-0670 or
              info@brixenfunding.com. Mobile opt-in information is never shared
              with third parties.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3" data-id="element-393">
              5. COOKIES AND TRACKING TECHNOLOGIES
            </h3>
            <p data-id="element-394">
              We use cookies, web beacons, and similar tracking technologies to
              customize the Site and improve your experience. You can modify
              your browser settings to decline cookies if you prefer, though
              this may limit your use of certain features of the Site.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3" data-id="element-395">
              6. DATA SECURITY
            </h3>
            <p data-id="element-396">
              We use administrative, technical, and physical security measures
              including SSL encryption and firewalls to help protect your
              personal information. However, please be aware that no electronic
              transmission over the Internet or information storage technology
              is 100% secure.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3" data-id="element-397">
              7. DATA RETENTION
            </h3>
            <p data-id="element-398">
              We will retain your personal information only for as long as is
              necessary for the purposes set out in this Privacy Policy, or as
              required by law.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3" data-id="element-399">
              8. YOUR RIGHTS AND OPT-OUT CHOICES
            </h3>
            <p className="mb-2" data-id="element-400">
              <strong data-id="element-401">Marketing Communications:</strong> You can unsubscribe
              from our marketing emails by clicking the "unsubscribe" link in
              the email.
            </p>
            <p className="mb-2" data-id="element-402">
              <strong data-id="element-403">SMS Opt-Out:</strong> As stated above, you may opt out of
              SMS messages at any time by replying "STOP".
            </p>
            <p data-id="element-404">
              <strong data-id="element-405">Access and Correction:</strong> You have the right to
              request copies of your personal data or request that we correct
              any information you believe is inaccurate.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3" data-id="element-406">
              9. THIRD-PARTY WEBSITES
            </h3>
            <p data-id="element-407">
              Our Site may contain links to third-party websites (e.g., lender
              portals). This Privacy Policy does not apply to those third-party
              sites. We encourage you to review the privacy policies of any
              third-party site you visit.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3" data-id="element-408">
              10. CHILDREN'S PRIVACY
            </h3>
            <p data-id="element-409">
              Our Services are not intended for individuals under the age of 18.
              We do not knowingly collect personal information from children
              under 13.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3" data-id="element-410">
              11. CHANGES TO THIS PRIVACY POLICY
            </h3>
            <p data-id="element-411">
              We may update this Privacy Policy from time to time. The updated
              version will be indicated by an updated "Revised" date.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3" data-id="element-412">
              12. CONTACT US
            </h3>
            <p className="mb-3" data-id="element-413">
              If you have questions or comments about this Privacy Policy,
              please contact us at:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200" data-id="element-414">
              <p className="font-semibold text-gray-900" data-id="element-415">Brixen Funding LLC</p>
              <p data-id="element-416">390 NE 191st St Ste 8123</p>
              <p data-id="element-417">Miami, FL 33179</p>
              <p data-id="element-418">(800) 326-0670</p>
              <p data-id="element-419">info@brixenfunding.com</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-50 border-t border-gray-200 py-8 mt-16" data-id="element-420">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-421">
          <button onClick={onBack} className="inline-flex items-center text-[#00E676] hover:text-[#00d169] font-semibold transition-colors" data-id="element-422">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-423" />
            Return to Home
          </button>
        </div>
      </div>
    </div>;
}